import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Loader2,
  Pencil,
  Save,
  X,
  Gamepad2,
  ClipboardList,
  UserCircle,
  History,
  FileText,
  Download,
} from 'lucide-react';
import { isGameMaster } from '../lib/utils';
import CharacterPDFMode from '../components/character/CharacterPDFMode';
import {
  HistoryTab,
  FieldRenderer,
  SheetSection,
} from '../components/character/CharacterSheetRenderer';

const API_URL = '/api';

// ── Page principale ────────────────────────────────────────────────────────
export default function CharacterSheetPage() {
  const { campaignId } = useParams();
  const navigate       = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();

  const [loading,            setLoading]            = useState(true);
  const [saving,             setSaving]             = useState(false);
  const [editMode,           setEditMode]           = useState(false);
  const [activeTab,          setActiveTab]          = useState('sheet'); // 'sheet' | 'history'

  const [systemInfo,         setSystemInfo]         = useState(null);
  const [template,           setTemplate]           = useState(null);
  const [mySheet,            setMySheet]            = useState(null);
  const [allSheets,          setAllSheets]          = useState([]);
  const [viewingUserId,      setViewingUserId]      = useState(null);
  const [formData,           setFormData]           = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [exportingPdf,       setExportingPdf]       = useState(false);

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ     = isGameMaster(userRole);

  // ── Chargement initial ──────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const systemId = currentCampaign?.system_id;
      if (!systemId) { setLoading(false); return; }

      const [sysRes, sheetsRes] = await Promise.all([
        axios.get(`${API_URL}/game-systems/${systemId}`),
        axios.get(`${API_URL}/campaigns/${campaignId}/character-sheets`),
      ]);
      setSystemInfo(sysRes.data);
      setAllSheets(sheetsRes.data);

      const mine = sheetsRes.data.find(s => s.user_id === user?.id);
      setMySheet(mine || null);

      if (mine) {
        setFormData(mine.data || {});
        const tplRes = await axios.get(`${API_URL}/system-templates/${mine.template_id}`);
        setTemplate(tplRes.data);
        setSelectedTemplateId(mine.template_id);
      } else {
        const tplsRes = await axios.get(`${API_URL}/game-systems/${systemId}/templates`);
        setAvailableTemplates(tplsRes.data);
        if (tplsRes.data.length > 0) {
          const def = tplsRes.data.find(tp => tp.is_default) || tplsRes.data[0];
          setSelectedTemplateId(def.id);
          setTemplate(def);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [campaignId, currentCampaign?.system_id, user?.id, language]);

  useEffect(() => { loadData(); }, [loadData]);

  // Quand le MJ clique sur une autre fiche
  const handleViewSheet = async (sheet) => {
    setViewingUserId(sheet.user_id);
    setActiveTab('sheet');
    if (sheet.template_id !== template?.id) {
      try {
        const r = await axios.get(`${API_URL}/system-templates/${sheet.template_id}`);
        setTemplate(r.data);
      } catch { /* ignore */ }
    }
    setFormData(sheet.data || {});
    setEditMode(false);
  };

  const handleViewMine = () => {
    setViewingUserId(null);
    setActiveTab('sheet');
    if (mySheet) {
      setFormData(mySheet.data || {});
      setEditMode(false);
    }
  };

  // ── Champ change ─────────────────────────────────────────────────────────
  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Sauvegarder ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      if (mySheet) {
        const res = await axios.patch(
          `${API_URL}/campaigns/${campaignId}/character-sheets/${mySheet.id}`,
          { data: formData, version: mySheet.version }  // optimistic locking
        );
        setMySheet(res.data);
        setAllSheets(prev => prev.map(s => s.id === res.data.id ? res.data : s));
      } else {
        const res = await axios.post(
          `${API_URL}/campaigns/${campaignId}/character-sheets`,
          { template_id: selectedTemplateId, data: formData }
        );
        setMySheet(res.data);
        setAllSheets(prev => [...prev, res.data]);
      }
      toast.success(t('success_saved', language));
      setEditMode(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData(mySheet?.data || {});
    setEditMode(false);
  };

  // ── Mise à jour de version depuis le mode PDF ─────────────────────────────
  const handlePdfVersionUpdate = useCallback((newVersion) => {
    setMySheet((prev) => (prev ? { ...prev, version: newVersion } : prev));
  }, []);

  // ── Export PDF ────────────────────────────────────────────────────────────
  const handleExportPdf = async () => {
    if (!currentSheetId) return;
    setExportingPdf(true);
    try {
      const res = await axios.get(
        `${API_URL}/campaigns/${campaignId}/character-sheets/${currentSheetId}/export/pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      const disp  = res.headers['content-disposition'] || '';
      const match = disp.match(/filename="?([^";\n]+)"?/i);
      a.href     = url;
      a.download = match?.[1] || `character_sheet_${currentSheetId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      toast.error(
        err.response?.status === 422
          ? t('pdf_mode_no_overlay', language)
          : t('pdf_mode_export_error', language)
      );
    } finally {
      setExportingPdf(false);
    }
  };

  // ── Données dérivées ─────────────────────────────────────────────────────
  const viewingSheet   = viewingUserId
    ? allSheets.find(s => s.user_id === viewingUserId)
    : mySheet;

  const currentSheetId = viewingSheet?.id ?? null;
  const displayData    = viewingUserId ? (viewingSheet?.data || {}) : formData;

  const viewerMember = currentCampaign?.members?.find(m => m.user_id === viewingUserId);
  const viewingName  = viewerMember?.character_name || viewerMember?.username
    || (viewingUserId ? viewingUserId.slice(0, 8) : null);

  // Sections visibles selon rôle
  const visibleSections = (template?.layout?.sections || []).filter(section => {
    if (isMJ) return true;
    if (!viewingUserId) return true;
    return (section.access?.other_players ?? 'read') !== 'hidden';
  });

  // Onglet historique : visible si fiche chargée ET (MJ OU propre fiche)
  const showHistoryTab = !!currentSheetId && (isMJ || !viewingUserId);
  const isReadOnly     = !!viewingUserId || !editMode;

  // Onglet PDF : visible si le template a un PDF overlay ET une fiche est chargée
  const hasPdfOverlay = !!(template?.pdf_asset_id && template?.pdf_overlay?.pages?.length > 0);
  const showPdfTab    = hasPdfOverlay && !!currentSheetId;

  // ── États de chargement ──────────────────────────────────────────────────
  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!currentCampaign?.system_id) {
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto mt-20 text-center space-y-4">
          <Gamepad2 className="h-14 w-14 mx-auto text-muted-foreground" />
          <h2 className="font-cinzel text-xl font-bold">{t('character_sheet', language)}</h2>
          <p className="text-muted-foreground">{t('character_sheet_no_system', language)}</p>
          {!isMJ && <p className="text-sm text-muted-foreground">{t('character_sheet_no_system_hint', language)}</p>}
          {isMJ && (
            <Button variant="outline" onClick={() => navigate(`/campaigns/${campaignId}`)}>
              {language === 'fr' ? 'Configurer le système' : 'Configure system'}
            </Button>
          )}
        </div>
      </MainLayout>
    );
  }

  if (!template) {
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto mt-20 text-center space-y-4">
          <ClipboardList className="h-14 w-14 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">{t('character_sheet_no_template', language)}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-5 animate-fade-in max-w-3xl mx-auto">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6" />
              {viewingUserId
                ? `${t('character_sheet_viewing', language)} ${viewingName}`
                : t('character_sheet', language)}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-xs">
                <Gamepad2 className="h-3 w-3 mr-1" />
                {systemInfo?.name}
              </Badge>
              {isMJ && (
                <span className="text-xs text-muted-foreground">
                  {t('character_sheet_gm_view', language)}
                </span>
              )}
              {!isMJ && !viewingUserId && (
                <span className="text-xs text-muted-foreground">
                  {editMode ? '' : t('character_sheet_readonly', language)}
                </span>
              )}
              {!isMJ && viewingUserId && (
                <span className="text-xs text-muted-foreground">
                  {t('character_sheet_other_player', language)}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Sélecteur de fiche (MJ ou si plusieurs membres) */}
            {(isMJ || allSheets.length > 1) && (
              <div className="flex gap-1 flex-wrap">
                {!viewingUserId ? (
                  <Badge variant="default" className="text-xs cursor-default">
                    <UserCircle className="h-3 w-3 mr-1" />
                    {language === 'fr' ? 'Ma fiche' : 'My sheet'}
                  </Badge>
                ) : (
                  <button
                    onClick={handleViewMine}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                  >
                    {language === 'fr' ? 'Ma fiche' : 'My sheet'}
                  </button>
                )}
                {allSheets.filter(s => s.user_id !== user?.id).map(s => {
                  const member    = currentCampaign?.members?.find(m => m.user_id === s.user_id);
                  const name      = member?.character_name || member?.username || s.user_id.slice(0, 6);
                  const isViewing = viewingUserId === s.user_id;
                  return isViewing ? (
                    <Badge key={s.id} variant="secondary" className="text-xs cursor-default">
                      <UserCircle className="h-3 w-3 mr-1" />{name}
                    </Badge>
                  ) : (
                    <button
                      key={s.id}
                      onClick={() => handleViewSheet(s)}
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Export PDF — visible si le template a un overlay PDF */}
            {showPdfTab && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPdf}
                disabled={exportingPdf}
              >
                {exportingPdf
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Download className="h-4 w-4 mr-1" />}
                {t('pdf_mode_export', language)}
              </Button>
            )}

            {/* Edit / Save / Cancel — uniquement sur ma propre fiche en onglet Fiche */}
            {!viewingUserId && activeTab === 'sheet' && (
              <>
                {editMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-1" />
                      {t('cancel', language)}
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving}>
                      {saving
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <Save    className="h-4 w-4 mr-1" />}
                      {t('character_sheet_save', language)}
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => setEditMode(true)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    {t('character_sheet_edit', language)}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Onglets ─────────────────────────────────────────────────────── */}
        <div className="flex border-b border-border gap-0">
          <button
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'sheet'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('sheet')}
          >
            <ClipboardList className="h-4 w-4" />
            {t('character_sheet', language)}
          </button>

          {showPdfTab && (
            <button
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'pdf'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('pdf')}
            >
              <FileText className="h-4 w-4" />
              {t('pdf_mode_tab', language)}
            </button>
          )}

          {showHistoryTab && (
            <button
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'history'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('history')}
            >
              <History className="h-4 w-4" />
              {t('history_tab', language)}
            </button>
          )}
        </div>

        {/* ── Onglet : Fiche ───────────────────────────────────────────────── */}
        {activeTab === 'sheet' && (
          <div className="space-y-4">
            {/* Sélecteur de template si pas encore de fiche */}
            {!mySheet && !viewingUserId && availableTemplates.length > 1 && (
              <Card className="glass-card border-primary/20">
                <CardContent className="p-4 space-y-2">
                  <Label>{t('character_sheet_select_template', language)}</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                    value={selectedTemplateId}
                    onChange={e => {
                      setSelectedTemplateId(e.target.value);
                      const chosen = availableTemplates.find(tp => tp.id === e.target.value);
                      setTemplate(chosen || null);
                    }}
                  >
                    {availableTemplates.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))}
                  </select>
                </CardContent>
              </Card>
            )}

            {/* Message si pas de fiche */}
            {!mySheet && !viewingUserId && (
              <Card className="glass-card border-dashed">
                <CardContent className="py-8 text-center space-y-3">
                  <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">{t('character_sheet_no_sheet', language)}</p>
                  {!editMode && (
                    <Button onClick={() => setEditMode(true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      {t('character_sheet_create', language)}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Sections de la fiche */}
            {(mySheet || viewingUserId || editMode) && visibleSections.map((section, idx) => (
              <SheetSection
                key={section.id}
                section={section}
                schema={template?.fields_schema}
                data={displayData}
                onChange={handleFieldChange}
                readOnly={isReadOnly}
                defaultOpen={idx === 0 || !section.collapsed_by_default}
              />
            ))}

            {/* Template info */}
            <p className="text-xs text-center text-muted-foreground pb-2">
              {template?.name} v{template?.version}
            </p>
          </div>
        )}

        {/* ── Onglet : Historique ──────────────────────────────────────────── */}
        {activeTab === 'history' && showHistoryTab && (
          <HistoryTab
            campaignId={campaignId}
            sheetId={currentSheetId}
            isMJ={isMJ}
            language={language}
          />
        )}

        {/* ── Onglet : PDF ─────────────────────────────────────────────────── */}
        {activeTab === 'pdf' && showPdfTab && (
          <CharacterPDFMode
            campaignId={campaignId}
            sheetId={currentSheetId}
            sheetVersion={viewingSheet?.version ?? mySheet?.version ?? 1}
            pdfAssetId={template.pdf_asset_id}
            pdfOverlay={template.pdf_overlay}
            builderAnnotations={template.builder_annotations || []}
            formData={displayData}
            onFormDataChange={viewingUserId ? () => {} : setFormData}
            onVersionUpdate={viewingUserId ? () => {} : handlePdfVersionUpdate}
            isActive={activeTab === 'pdf'}
            isMJ={isMJ}
            isReadOnly={!!viewingUserId}
            language={language}
          />
        )}

      </div>
    </MainLayout>
  );
}
